
import { Modal, Box, Typography, List, ListItem, ListItemText, IconButton, Button, CardHeader, Card, CardMedia, CardContent, Link } from '@mui/material';
import type { Playlist } from '@spotify/web-api-ts-sdk';
import { SpotifyPlaylist } from "@ds-types/spotify";
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect } from 'react';
import { getLocalStorage, setLocalStorage } from '@ds-services/helpers';
import { getCreatedPlaylist } from '@ds-services/spotify/helpers';
import { GoldButton, dotaSpottoTheme } from '@ds-components/General/dotaspottotheme';
import { HeroLookup } from '@ds-types/dotes';
// import { maxWidth } from '@mui/system';

interface PlaylistModalProps {
   playlist: {
      thehero: HeroLookup;
      theplaylist: SpotifyPlaylist;
   } | boolean;
   text?: string;
}
const isSpotifyPlaylist = (value: any): value is SpotifyPlaylist => {
   return (
      value !== null &&
      typeof value === 'object' &&
      'id' in value &&
      'snapshot_id' in value &&
      'name' in value &&
      'uri' in value
   );
}

const SpotifyModal: React.FC<PlaylistModalProps> = ({ playlist, text }) => {
   const [modalOpen, setModalOpen] = React.useState(false);
   const handleOpen = () => setModalOpen(true);
   const handleClose = () => setModalOpen(false);

   const [createdPlaylist, setCreatedPlaylist] = React.useState<SpotifyPlaylist | null>(null);

   const isPlaylist = playlist && typeof playlist !== "boolean" && isSpotifyPlaylist(playlist.theplaylist);
   useEffect(() => {
      if (isPlaylist) {
         setModalOpen(true);
      }
   }, [isPlaylist]);
   useEffect(() => {
      const fetchPlaylist = async () => {
         const generatedPlaylist = typeof playlist !== 'boolean' ? playlist.theplaylist : null;
         const generatedHero = typeof playlist !== 'boolean' ? playlist.thehero : null;
         if (typeof playlist !== "boolean" && isSpotifyPlaylist(generatedPlaylist)) {
            try {
               const playlistDetails = await getCreatedPlaylist(generatedPlaylist);
               if (playlistDetails) {
                  setCreatedPlaylist(playlistDetails);
                  // <=== 💸  Add Playlist to Local Storage  💸 ===>
                  const savedPlaylists = getLocalStorage("saved_playlists");
                  const playlistsArray = savedPlaylists ? JSON.parse(savedPlaylists) : [];
                  const alreadyExists = playlistsArray.some((p: any) => p.id === playlistDetails.id);
                  if (!alreadyExists) {
                     playlistsArray.unshift({
                        id: playlistDetails.id,
                        name: playlistDetails.name,
                        image: playlistDetails.images[0]?.url,
                        url: playlistDetails.external_urls.spotify,
                        uri: playlistDetails.uri,
                        description: playlistDetails.description,
                        hero: {
                           avatar: generatedHero?.img,
                           video: generatedHero?.video
                        }
                     });
                     setLocalStorage("saved_playlists", JSON.stringify(playlistsArray));
                  }
               }
            } catch (error) {
               console.error('Error fetching playlist:', error);
            }
         }
      };
      fetchPlaylist();
   }, [playlist]);

   if (isPlaylist) {
      return (
         <>
            <Button onClick={handleOpen}
               variant="contained"
               size="large"
            >
               View Playlist Details
            </Button>

            <Modal
               open={modalOpen}
               onClose={handleClose}
               aria-labelledby="playlist-modal-title"
            >
               <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: {
                     lg: 'fit-content',
                     xs: '100%'
                  },
                  // bgcolor: 'background.paper',
                  // padding: 5,
                  boxShadow: 24,
                  color: 'black',
                  // p: 4,
                  borderRadius: 2,
                  maxHeight: '80vh',
                  overflow: 'auto'
               }}>
                  <Box
                     sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: "column",
                        // m: 2,
                        // backgroundColor: 'background.paper'
                     }}
                  >
                     {createdPlaylist && (
                        <Card sx={{ maxWidth: 369 }}>
                           <CardHeader
                              action={
                                 <IconButton onClick={handleClose} size="small">
                                    <CloseIcon />
                                 </IconButton>
                              }
                              title={
                                 <Typography id="playlist-modal-title" variant="h6" component="h2" sx={{
                                    fontWeight: 'bold',
                                    fontFamily: dotaSpottoTheme.font2,
                                 }}>
                                    {createdPlaylist.name}
                                 </Typography>
                              }
                              subheader={
                                 <>
                                    <Typography sx={{
                                       // backgroundColor: 'background.paper',
                                       color: 'white',
                                       fontFamily: dotaSpottoTheme.font
                                    }}>
                                       Just For: <strong>{createdPlaylist.owner.display_name}</strong>
                                    </Typography>
                                 </>

                              }
                           >
                           </CardHeader>
                           <CardMedia
                              component="img"
                              height={createdPlaylist.images[0].height}
                              width={createdPlaylist.images[0].width}
                              image={createdPlaylist.images[0].url}
                              style={{ aspectRatio: 1 / 1, height: 'auto' }}
                           />
                           <CardContent>
                              <Typography sx={{
                                 // backgroundColor: 'background.paper',
                                 color: 'white',
                                 fontFamily: dotaSpottoTheme.font,
                                 mb: 2
                              }}>
                                 {createdPlaylist.description}
                              </Typography>
                              <Link
                                 href={createdPlaylist.external_urls.spotify}
                                 target="_blank"
                                 variant='button'
                                 rel="noopener noreferrer">
                                 View Your Playlist
                              </Link>
                           </CardContent>
                        </Card>
                     )}
                  </Box>
               </Box>
            </Modal>
         </>
      );
   }
   if (text) {
      return (
         <>
            <Modal
               open={modalOpen}
               onClose={handleClose}
               aria-labelledby="playlist-modal-title"
            >
               <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 800,
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  color: 'black',
                  p: 4,
                  borderRadius: 2,
                  maxHeight: '80vh',
                  overflow: 'auto'
               }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection="column" mb={2}>
                     <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                     </IconButton>
                     {text}
                  </Box>
               </Box>
            </Modal>
         </>
      )

   }
   return "";

};

export default SpotifyModal;